import React, { useState, useEffect } from 'react'
import ScrollToVersionItem from './ScrollToVersionItem'

interface VersionInfo {
  version: string
  slug: string
}

interface ScrollToVersionProps {
  versions: VersionInfo[]
}

const ScrollToVersion: React.FC<ScrollToVersionProps> = React.memo(
  ({ versions }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedVersion, setSelectedVersion] = useState<string>(
      versions[0]?.version || '',
    )

    // Track which version is currently visible
    useEffect(() => {
      const handleScroll = () => {
        const versionElements = versions.map((v) =>
          document.getElementById(`v${v.slug}`),
        )
        const viewportTop = window.scrollY
        const viewportBottom = viewportTop + window.innerHeight

        // Find the version that's most visible in the viewport
        let mostVisibleVersion = versions[0]?.version || ''
        let maxVisibility = 0

        versionElements.forEach((element, index) => {
          if (element) {
            const rect = element.getBoundingClientRect()
            const elementTop = rect.top + window.scrollY
            const elementBottom = elementTop + rect.height

            // Calculate how much of the element is visible
            const visibleTop = Math.max(viewportTop, elementTop)
            const visibleBottom = Math.min(viewportBottom, elementBottom)
            const visibleHeight = Math.max(0, visibleBottom - visibleTop)
            const visibility = visibleHeight / rect.height

            if (visibility > maxVisibility) {
              maxVisibility = visibility
              mostVisibleVersion = versions[index]?.version || ''
            }
          }
        })

        setSelectedVersion(mostVisibleVersion)
      }

      window.addEventListener('scroll', handleScroll)
      handleScroll() // Initial check

      return () => window.removeEventListener('scroll', handleScroll)
    }, [versions])

    const handleVersionSelect = (version: string, slug: string) => {
      setSelectedVersion(version)
      setIsOpen(false)

      // Smooth scroll to the selected version
      const element = document.getElementById(`v${slug}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    return (
      <div className="changelog-scroll-to relative inline-block">
        <button
          className="text-sm cursor-pointer flex items-center px-3 py-2 rounded border focus:outline-none focus:ring-2"
          style={{
            color: 'var(--changelog-text)',
            backgroundColor: 'var(--changelog-input-bg)',
            borderColor: 'var(--changelog-border)',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--changelog-input-focus)'
            e.target.style.boxShadow = `0 0 0 2px var(--changelog-input-focus)25`
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--changelog-border)'
            e.target.style.boxShadow = 'none'
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="mr-2">v{selectedVersion}</span>
          <i className={`fa fa-angle-${isOpen ? 'up' : 'down'}`} />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <ul
              className="changelog-scroll-to-list absolute mt-1 min-w-[120px] z-20 rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                backgroundColor: 'var(--changelog-input-bg)',
                border: '1px solid var(--changelog-border)',
                boxShadow: 'var(--changelog-shadow)',
                top: '100%',
                left: 0,
              }}
            >
              {versions &&
                versions.length > 0 &&
                versions.map(({ version, slug }, index) => (
                  <ScrollToVersionItem
                    key={`${version}-${index}`}
                    version={version}
                    slug={slug}
                    isSelected={version === selectedVersion}
                    onClick={() => handleVersionSelect(version, slug)}
                  />
                ))}
            </ul>
          </>
        )}
      </div>
    )
  },
)

export default ScrollToVersion
